import { CATALOG_OVERRIDE_KEY, RISK_SUBFACTOR_CATALOGS, computeRiskScore, effectiveMatrixForMode, } from './risk-score.js';
/**
 * Resolve one catalog-key input to its band, firing the corresponding OVERRIDE
 * (raise-the-floor) when the chosen entry is marked `override: true` — e.g. a
 * FATF-black country (`fatfNegra`) or `antecedentes=condena_precedente`
 * (`antecedente`). Absent / unknown key → `bajo` (uncaptured data never inflates).
 */
function pick(inputs, field, catalogKey, overrides) {
    const key = inputs[field];
    if (typeof key !== 'string')
        return 'bajo';
    const entry = RISK_SUBFACTOR_CATALOGS[catalogKey]?.find((e) => e.key === key);
    if (!entry)
        return 'bajo';
    if (entry.override) {
        const ov = CATALOG_OVERRIDE_KEY[catalogKey];
        if (ov)
            overrides[ov] = true;
    }
    return entry.band;
}
/**
 * Map persisted catalog keys → the bands `computeRiskScore` consumes, plus the
 * overrides those keys auto-fire. The four explicit override booleans on
 * `risk_inputs` are folded in too (a floor is monotone: OR-ing never lowers a
 * nivel); pep/sanciones may also arrive externally via `computeExpedienteRisk`.
 */
export function deriveRiskLevels(inputs = {}) {
    const overrides = {};
    const levels = {
        cliente: {
            profesion: pick(inputs, 'profesion', 'cliente.profesion', overrides),
            estructura: pick(inputs, 'estructura', 'cliente.estructura', overrides),
            antecedentes: pick(inputs, 'antecedentes', 'cliente.antecedentes', overrides),
        },
        jurisdiccion: {
            nacionalidad: pick(inputs, 'nacionalidad', 'jurisdiccion.nacionalidad', overrides),
            origen_destino: pick(inputs, 'origen_destino', 'jurisdiccion.origen_destino', overrides),
            domicilio: pick(inputs, 'domicilio', 'jurisdiccion.domicilio', overrides),
            listas: pick(inputs, 'listas', 'jurisdiccion.listas', overrides),
        },
        canal: {
            canal_via: pick(inputs, 'canal_via', 'canal.canal_via', overrides),
            frecuencia: pick(inputs, 'frecuencia', 'canal.frecuencia', overrides),
            monto: pick(inputs, 'monto', 'canal.monto', overrides),
            instrumento: pick(inputs, 'instrumento', 'canal.instrumento', overrides),
        },
        producto: pick(inputs, 'producto', 'producto', overrides),
    };
    if (inputs.sanciones)
        overrides.sanciones = true;
    if (inputs.pep)
        overrides.pep = true;
    if (inputs.fatfNegra)
        overrides.fatfNegra = true;
    if (inputs.antecedente)
        overrides.antecedente = true;
    return { levels, overrides };
}
/**
 * Compose `deriveRiskLevels` with the EXTERNAL overrides (person-level PEP from the
 * subject_owners aggregation; sanciones from screening) and score against the
 * mode-effective matrix. The one place expediente state becomes a nivel/score.
 */
export function computeExpedienteRisk(input) {
    const { levels, overrides } = deriveRiskLevels(input.riskInputs ?? {});
    const merged = { ...overrides };
    if (input.pepPropio || input.pepFamiliar)
        merged.pep = true;
    if (input.sancionesHit)
        merged.sanciones = true;
    return computeRiskScore(effectiveMatrixForMode(input.entityMode, input.matrix), levels, merged);
}
/**
 * Aggregate the per-person PEP flags across ALL related persons of an expediente
 * (directors / accionistas>umbral / BF / fundador …) into the case-level override.
 * // ponytail: heurístico max-band (any person PEP ⇒ case PEP); upgrade = promedio
 * ponderado por porcentaje_patrimonial si cumplimiento lo pide.
 */
export function aggregatePersonRisk(owners) {
    return {
        pepPropio: owners.some((o) => o.pepPropio === true),
        pepFamiliar: owners.some((o) => o.pepFamiliar === true),
    };
}
//# sourceMappingURL=risk-derive.js.map